import { RoleId } from '@common/constants';
import type { JSONCompatible } from '@common/types';
import type {
    QuestionSchema,
    QuestionValidationSchema,
    TemplateFormField,
} from '@common/types/templateFormFields';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
    Box,
    IconButton,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import type { DragEvent, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import useIdentityStore from '@/stores/useIdentityStore';
import FormFieldInput from './FormFieldInput';
import FormFieldLabel from './FormFieldLabel';
import { conditionalSectionStyle, formFieldContainerStyle } from './formFieldStyles';

interface Props {
    fieldObj: TemplateFormField;
    allQuestions: TemplateFormField[];
    lawFirmId: string | number;
    isRenderFrenchStaticValue?: boolean;
    value?: JSONCompatible;
    labelStyles?: object;
    isError?: boolean;
    helper?: string;
    tabindex?: string | number;
    containerPadding?: string;
    disabled?: boolean;
    readOnly?: boolean;
    formGroupIndex?: number;
    shouldRenderAsterisk?: boolean;
    shouldRenderCritical?: boolean;
    typeOfIdValue?: string;
    isCriticalQuestion?: boolean;
    showSelectBoxValue?: boolean;
    includeFieldAutomationRequired?: boolean;
    isFieldActionEnabled?: boolean;
    isVisible?: boolean;
    isLocked?: boolean;
    isHideDisabled?: boolean;
    onVisibilityChange?: (isVisible: boolean) => void;
    onOrderChange?: (order: {
        originalOrder: number;
        newOrder: number;
        formGroupIndex: number;
    }) => void;
    onEditClick?: (fieldObj: TemplateFormField) => void;
    onDeleteClick?: (field: TemplateFormField) => void;
    onFieldValueChange?: (name: string, value: JSONCompatible) => void;
}

export default function BuilderFormComponent({
    fieldObj,
    allQuestions,
    value,
    labelStyles,
    isError,
    helper,
    tabindex,
    shouldRenderAsterisk = true,
    showSelectBoxValue,
    disabled,
    readOnly,
    formGroupIndex,
    containerPadding,
    typeOfIdValue,
    shouldRenderCritical = true,
    isCriticalQuestion,
    includeFieldAutomationRequired = false,
    isFieldActionEnabled = true,
    isVisible,
    isLocked,
    isHideDisabled,
    onVisibilityChange,
    onOrderChange,
    onEditClick,
    onDeleteClick,
    onFieldValueChange,
    lawFirmId,
    isRenderFrenchStaticValue = false,
}: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const user = useIdentityStore((state) => state.user);
    const isSuperAdmin = !!user?.roles?.some((role) => role.roleId === RoleId.SUPER_ADMIN);
    const [shouldShow, setShouldShow] = useState<boolean>(false);

    const questionSchema = fieldObj.questionSchema;
    const fieldOrder = fieldObj.displayOrder ?? questionSchema.order;

    useEffect(() => {
        setShouldShow(!!isVisible);
    }, [isVisible]);

    const labelOpacity = !shouldShow ? '0.35' : '1';
    const contentOpacity = isFieldActionEnabled && !shouldShow ? '0.35' : '1';
    const conditionalGridColumns = isFieldActionEnabled ? '98px 1fr 132px' : undefined;

    const readyToDrag = (icon: Element, enabled: boolean) => {
        if (!shouldShow) return;
        // The handle is an SVG, and the painted path is the event target. Find the
        // drop area from the icon element so draggable is set on the element that
        // owns the drag handlers.
        const dropArea = icon.closest('.mainDropArea');
        if (!dropArea) return;
        if (enabled) {
            dropArea.setAttribute('draggable', 'true');
        } else {
            dropArea.removeAttribute('draggable');
        }
    };

    const toggleHide = () => {
        setShouldShow(!shouldShow);
        onVisibilityChange?.(!shouldShow);
    };

    const initLabelEdit = (question: TemplateFormField) => {
        if (!shouldShow) return;
        onEditClick?.(question);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        const originalOrder = event.dataTransfer?.getData('text/plain');
        const newOrder = fieldOrder?.toString();
        if (originalOrder && newOrder && onOrderChange) {
            onOrderChange({
                originalOrder: parseInt(JSON.parse(originalOrder).order, 10),
                newOrder: parseInt(newOrder, 10),
                formGroupIndex: parseInt(JSON.parse(originalOrder).formGroupIndex, 10),
            });
        }
    };

    const renderFieldActions = (formComponentData: QuestionSchema) => (
        <Box
            sx={{
                display: 'flex',
                // Contact blocks are taller than they are wide, so stack their actions
                ...(formComponentData.type === 'contactInfoSection'
                    ? { flexDirection: 'column', gap: '8px' }
                    : { gap: '24px' }),
                alignItems: 'center',
            }}
        >
            <Tooltip
                arrow
                placement="top"
                title={shouldShow ? 'Edit Label' : null}
                enterDelay={1000}
            >
                <IconButton
                    onClick={() => {
                        const question = allQuestions.find(
                            (q) => q.questionSchema.name === formComponentData.name,
                        );
                        if (question) {
                            initLabelEdit(question);
                        }
                    }}
                    sx={{
                        color: 'neutral.N_500',
                        opacity: `${shouldShow ? '1' : '0.35'}`,
                        cursor: shouldShow ? 'pointer' : 'not-allowed',
                    }}
                >
                    <EditOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
            </Tooltip>
            {!formComponentData.isConditionalQuestion && (
                <Tooltip
                    arrow
                    enterDelay={1000}
                    placement="top"
                    title={
                        !isLocked
                            ? shouldShow
                                ? 'Disable'
                                : 'Enable'
                            : 'Show/hide action is locked'
                    }
                >
                    <IconButton
                        onClick={() => {
                            if (!isLocked) {
                                toggleHide();
                            }
                        }}
                        sx={{ color: 'neutral.N_500' }}
                        disabled={isHideDisabled || isCriticalQuestion}
                    >
                        {!isLocked ? (
                            shouldShow ? (
                                <VisibilityOffIcon />
                            ) : (
                                <VisibilityIcon />
                            )
                        ) : (
                            <LockIcon />
                        )}
                    </IconButton>
                </Tooltip>
            )}
            {!isSuperAdmin &&
                (formComponentData.isCustomQuestion || formComponentData.isConditionalQuestion) && (
                    <Tooltip
                        arrow
                        enterDelay={1000}
                        placement="top"
                        title={
                            !isLocked || formComponentData.isConditionalQuestion
                                ? 'Delete'
                                : 'Delete action is locked'
                        }
                    >
                        <IconButton
                            onClick={() => {
                                const question = allQuestions.find(
                                    (q) => q.questionSchema.name === formComponentData.name,
                                );
                                if (
                                    (!isLocked || formComponentData.isConditionalQuestion) &&
                                    question &&
                                    onDeleteClick
                                ) {
                                    onDeleteClick(question);
                                }
                            }}
                            sx={{ color: theme.palette.neutral.N_500 }}
                        >
                            {!isLocked || formComponentData.isConditionalQuestion ? (
                                <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                            ) : (
                                <LockIcon />
                            )}
                        </IconButton>
                    </Tooltip>
                )}
            {isSuperAdmin &&
                (!formComponentData.isCustomQuestion ||
                    !formComponentData.isConditionalQuestion) && (
                    <Tooltip arrow enterDelay={1000} placement="top" title={'Delete'}>
                        <IconButton
                            onClick={() => {
                                const question = allQuestions.find(
                                    (q) => q.questionSchema.name === formComponentData.name,
                                );
                                if (question && onDeleteClick) {
                                    onDeleteClick(question);
                                }
                            }}
                            sx={{ color: theme.palette.neutral.N_500 }}
                        >
                            <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                )}
        </Box>
    );

    const showRearrangeIcon = isFieldActionEnabled && !questionSchema.isConditionalQuestion;

    const leadingAdornment: ReactNode = showRearrangeIcon ? (
        <DragIndicatorIcon
            style={{
                cursor: shouldShow ? 'grab' : 'not-allowed',
                opacity: `${shouldShow ? '1' : '0.35'}`,
                fontSize: 16,
            }}
            onMouseEnter={(event) => {
                readyToDrag(event.currentTarget, true);
            }}
            onMouseLeave={(event) => {
                readyToDrag(event.currentTarget, false);
            }}
        />
    ) : null;

    const renderFieldRow = (
        schema: QuestionSchema,
        label: string,
        validation?: QuestionValidationSchema | null,
        fieldValue?: JSONCompatible,
        trailingAdornment?: ReactNode,
        showLeadingAdornment = true,
    ) => (
        <Box
            sx={{
                ...formFieldContainerStyle,
                alignItems: 'center',
                padding: containerPadding
                    ? containerPadding
                    : isMobile
                      ? '0.5rem 0.5rem'
                      : '0.75rem 1.5rem',
                boxSizing: 'border-box',
                width: '100%',
            }}
        >
            {!schema.isConditionalQuestion && showLeadingAdornment && leadingAdornment}

            <Stack sx={{ width: '100%' }}>
                <FormFieldLabel
                    schema={schema}
                    label={label}
                    validation={validation}
                    labelStyles={labelStyles}
                    disabled={disabled}
                    shouldRenderAsterisk={shouldRenderAsterisk}
                    shouldRenderCritical={shouldRenderCritical}
                    isCriticalQuestion={isCriticalQuestion}
                    includeFieldAutomationRequired={includeFieldAutomationRequired}
                    isRequiredForFullAutomation={fieldObj.isRequiredForFullAutomation}
                    opacity={labelOpacity}
                    language={isRenderFrenchStaticValue ? 'fr' : 'en'}
                />

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: '5px',
                    }}
                >
                    <Box sx={{ width: '100%', opacity: contentOpacity }}>
                        <FormFieldInput
                            schema={schema}
                            validation={validation}
                            value={fieldValue}
                            isError={isError}
                            helper={helper}
                            tabindex={tabindex}
                            disabled={disabled}
                            readOnly={readOnly}
                            showSelectBoxValue={showSelectBoxValue}
                            typeOfIdValue={typeOfIdValue}
                            lawFirmId={lawFirmId}
                            language={isRenderFrenchStaticValue ? 'fr' : 'en'}
                            onFieldValueChange={onFieldValueChange}
                        />
                    </Box>
                    {trailingAdornment}
                </Box>
            </Stack>
        </Box>
    );

    const getNote = (queName: string) => {
        if (!queName) return '';
        const matchedQuestion = allQuestions.find((que) => que.questionSchema.name === queName);
        return matchedQuestion?.questionSchema.note ?? '';
    };

    const renderConditionalSection = (queArr: string[]) => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: conditionalGridColumns ?? '1fr',
                width: '100%',
                alignItems: 'center',
            }}
        >
            {conditionalGridColumns && <div />}
            <Box
                sx={{
                    ...conditionalSectionStyle(theme.palette.neutral.borderColor),
                    width: '100%',
                }}
            >
                <Typography sx={{ marginBottom: '16px' }} variant="body3">
                    {Array.isArray(queArr) && queArr[0] && getNote(queArr[queArr.length - 1])}
                </Typography>
                {Array.isArray(queArr) &&
                    queArr.map((queName) => {
                        const matchedQuestion = allQuestions.find(
                            (que) => que.questionSchema.name === queName,
                        );
                        if (!matchedQuestion) return null;
                        return renderFieldRow(
                            matchedQuestion.questionSchema,
                            matchedQuestion.title,
                            matchedQuestion.questionValidationSchema,
                            matchedQuestion.questionSchema.value,
                            isFieldActionEnabled
                                ? renderFieldActions(matchedQuestion.questionSchema)
                                : undefined,
                            false,
                        );
                    })}
            </Box>
            {conditionalGridColumns && <div />}
        </Box>
    );

    const conditionsMap: Record<string, string[]> = {};
    const conditionOrder: string[] = [];
    for (const condition of questionSchema.conditions ?? []) {
        const nextQuestionName = condition.nextQuestion?.name ?? '';
        if (conditionsMap[condition.value]) {
            conditionsMap[condition.value].push(nextQuestionName);
        } else {
            conditionsMap[condition.value] = [nextQuestionName];
            conditionOrder.push(condition.value);
        }
    }

    const customConditionalProps = {
        ...(isError && {
            'data-is-error': fieldObj.displayOrder || true,
            'data-formgroup-index': formGroupIndex,
        }),
    };

    const dragHandlers = isFieldActionEnabled
        ? {
              className: 'mainDropArea',
              onDrop: (event: DragEvent<HTMLDivElement>) => {
                  handleDrop(event);
                  event.currentTarget.classList.remove('dropReady');
              },
              onDragStart: (event: DragEvent<HTMLDivElement>) => {
                  event.currentTarget.classList.add('draggedItem');
                  event.dataTransfer?.setData(
                      'text/plain',
                      JSON.stringify({
                          order: fieldOrder?.toString(),
                          formGroupIndex: formGroupIndex,
                      }),
                  );
              },
              onDragEnd: (event: DragEvent<HTMLDivElement>) => {
                  event.currentTarget.classList.remove('draggedItem');
                  const dropReadyEls = document.querySelectorAll('.dropReady');
                  dropReadyEls.forEach((el) => {
                      el.classList.remove('dropReady');
                  });
              },
              onDragOverCapture: (event: DragEvent<HTMLDivElement>) => {
                  event.preventDefault();
              },
              onDragEnter: (event: DragEvent<HTMLDivElement>) => {
                  event.preventDefault();
                  event.stopPropagation();
              },
              onDragLeave: (event: DragEvent<HTMLDivElement>) => {
                  if (event.currentTarget.classList.contains('dropReady')) {
                      event.currentTarget.classList.remove('dropReady');
                  }
              },
              onDragOver: (event: DragEvent<HTMLDivElement>) => {
                  if (questionSchema.isConditionalQuestion) {
                      return;
                  }
                  event.currentTarget.classList.add('dropReady');
              },
          }
        : undefined;

    return (
        <Box {...customConditionalProps} {...dragHandlers} sx={{ width: '100%' }}>
            {renderFieldRow(
                questionSchema,
                fieldObj.title,
                fieldObj.questionValidationSchema,
                value,
                isFieldActionEnabled ? renderFieldActions(questionSchema) : undefined,
            )}
            <Stack spacing={3}>
                {conditionOrder.map((conditionValue) => {
                    const matchedQuestion = allQuestions.find((que) =>
                        conditionsMap[conditionValue].includes(que.questionSchema.name),
                    );
                    if (!matchedQuestion) return null;
                    return renderConditionalSection(conditionsMap[conditionValue]);
                })}
            </Stack>
        </Box>
    );
}
